'use client'

import { useTranslations } from 'next-intl'
import { LocaleLink } from '@/components/LocaleLink'
import { useCallback, useEffect, useRef, useState } from 'react'

import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/ui'

import type { HomeHeaderFilterButton } from './types'

type Props = {
  buttons: HomeHeaderFilterButton[]
  activeId: string
  onSelect: (button: HomeHeaderFilterButton) => void
  loading?: boolean
  className?: string
  ariaLabel?: string
}

/**
 * Barra de filtros do Header Filter (paridade JNews). Botões trocam os posts da faixa via AJAX.
 * Progressive enhancement: itens com `href` (categoria/tag) são links de arquivo no no-JS; com JS,
 * o clique é interceptado e faz o filtro in-place.
 */
export function HomeHeaderFilterBar({
  buttons,
  activeId,
  onSelect,
  loading = false,
  className,
  ariaLabel,
}: Props) {
  const t = useTranslations('Home')
  const resolvedAriaLabel = ariaLabel ?? t('filterNews')
  const scrollRef = useRef<HTMLUListElement>(null)
  const [scrollEdges, setScrollEdges] = useState({ left: false, right: false })

  const updateScrollEdges = useCallback(() => {
    const el = scrollRef.current
    if (!el) return
    const maxScroll = el.scrollWidth - el.clientWidth
    setScrollEdges({
      left: el.scrollLeft > 2,
      right: maxScroll > 2 && el.scrollLeft < maxScroll - 2,
    })
  }, [])

  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    updateScrollEdges()
    const observer = new ResizeObserver(updateScrollEdges)
    observer.observe(el)
    return () => observer.disconnect()
  }, [updateScrollEdges, buttons])

  if (buttons.length === 0) return null

  const buttonClass =
    'h-8 shrink-0 snap-start rounded-full px-3.5 text-xs font-semibold tracking-wide'

  return (
    <nav
      aria-label={resolvedAriaLabel}
      aria-busy={loading || undefined}
      data-home-region="header-filter"
      className={cn('relative', className)}
    >
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 left-0 z-10 w-12 bg-gradient-to-r from-background via-background/95 to-transparent transition-opacity duration-200 max-lg:block lg:hidden',
          scrollEdges.left ? 'opacity-100' : 'opacity-0',
        )}
      />
      <div
        aria-hidden
        className={cn(
          'pointer-events-none absolute inset-y-0 right-0 z-10 w-12 bg-gradient-to-l from-background via-background/95 to-transparent transition-opacity duration-200 max-lg:block lg:hidden',
          scrollEdges.right ? 'opacity-100' : 'opacity-0',
        )}
      />

      <ul
        ref={scrollRef}
        onScroll={updateScrollEdges}
        className="flex touch-pan-x gap-2 overflow-x-auto scroll-smooth pb-0.5 max-lg:snap-x max-lg:snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden lg:flex-wrap lg:overflow-visible"
      >
        {buttons.map((button) => {
          const isActive = button.id === activeId
          const handleClick = (e: React.MouseEvent) => {
            // Progressive enhancement: intercepta o link de arquivo e filtra in-place.
            e.preventDefault()
            onSelect(button)
          }

          return (
            <li key={button.id} className="shrink-0 snap-start">
              {button.href ? (
                <Button
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  className={buttonClass}
                  asChild
                >
                  <LocaleLink
                    href={button.href}
                    onClick={handleClick}
                    aria-current={isActive ? 'true' : undefined}
                  >
                    {button.label}
                  </LocaleLink>
                </Button>
              ) : (
                <Button
                  type="button"
                  variant={isActive ? 'default' : 'outline'}
                  size="sm"
                  className={buttonClass}
                  aria-current={isActive ? 'true' : undefined}
                  onClick={() => onSelect(button)}
                >
                  {button.label}
                </Button>
              )}
            </li>
          )
        })}
      </ul>
    </nav>
  )
}
